import type { GalleryCardType, GalleryDetailType } from "@volgo/core/contract";
import { type ReactNode, useRef, useState } from "react";
import { Link, type To, useLocation } from "react-router";
import { ConfirmWarningButton, useAcks } from "../policy/ack";
import { useServerBlocks } from "../policy/blocked";
import { splitTag } from "../search/query";
import { useFocusReturn } from "./focusReturn";
import { formatPages, formatRating } from "./format";
import { categoryStyle } from "./labels";

/**
 * The cover address of a gallery, taken from the server's answer and from nowhere else (U-15b).
 * A blocked gallery's answer has no `thumb` field, so there is nothing to show; the client never builds
 * an address and never looks at tags to decide.
 */
export function coverOf(gallery: GalleryCardType | GalleryDetailType): string | null {
  return "thumb" in gallery ? gallery.thumb : null;
}

/** The cover picture. `veiled` blurs it (a warned gallery the user has not confirmed). */
export function Cover({ src, veiled = false }: { src: string; veiled?: boolean }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <img
      className={veiled ? "art art-veiled" : "art"}
      src={src}
      alt=""
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      data-veiled={veiled ? "true" : undefined}
      onError={() => setFailed(true)}
    />
  );
}

/**
 * The blurred glow behind the gallery window's cover: the same address as the visible cover, so it costs
 * no extra request. The caller draws it only while that cover is shown unveiled (46 Spec 3.3, 5).
 */
export function Ambient({ src }: { src: string }) {
  return (
    <img
      className="dw-amb"
      src={src}
      alt=""
      aria-hidden="true"
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      data-testid="ambient"
    />
  );
}

/** `guro, insect`: the names of the tags a policy matched, without namespaces. */
export function tagNames(tags: readonly string[]): string {
  return tags.map((t) => splitTag(t).name).join(", ");
}

/**
 * A gallery in a grid, in one of three states, each decided by the server's answer alone:
 *   none   the cover
 *   warn   the cover blurred, the matched tags and a confirm button
 *   block  no picture at all (hatching) and "차단된 태그가 포함되어 있습니다"
 * A card whose own data is older than a refusal the server gave since (`useServerBlocks`) is drawn blocked.
 */
export function GalleryCard({
  card,
  to,
  state,
  children,
  coverOverlay,
}: {
  card: GalleryCardType;
  /** where the card leads: the gallery window */
  to: To;
  state?: unknown;
  /** extra lines under the card (when it was added, a remove button) */
  children?: ReactNode;
  coverOverlay?: ReactNode;
}) {
  const location = useLocation();
  const acks = useAcks();
  const blocks = useServerBlocks();
  const cover = blocks.isBlocked(card.gid) ? null : coverOf(card);
  const blocked = cover === null;
  const veiled = card.policy.level === "warn" && !acks.isConfirmed(card.gid);
  const link = useRef<HTMLAnchorElement>(null);
  useFocusReturn(card.gid, link);

  return (
    <article
      className={blocked ? "card card-block" : "card"}
      data-testid="card"
      data-policy={blocked ? "block" : card.policy.level}
      data-gid={card.gid}
      style={blocked ? undefined : categoryStyle(card.category)}
    >
      <Link
        ref={link}
        className="card-link"
        aria-label={card.title}
        to={to}
        // a caller may name the background itself (cards inside the gallery window keep the list under it)
        state={{ inApp: true, background: location, ...(typeof state === "object" ? state : {}) }}
      >
        <div className={blocked ? "cv hatch" : "cv"}>
          {cover !== null && <Cover key={cover} src={cover} veiled={veiled} />}
          {!blocked && !veiled && coverOverlay}
          {blocked && (
            <div className="gate">
              <span>차단된 태그가 포함되어 있습니다</span>
            </div>
          )}
        </div>
        <h3>{card.title}</h3>
      </Link>
      {!blocked && veiled && card.policy.level === "warn" && (
        <div className="gate gate-warn">
          <b>경고 태그</b>
          <span>{tagNames(card.policy.matchedTags)}</span>
          <ConfirmWarningButton gid={card.gid} />
        </div>
      )}
      <p className="card-meta">
        {card.state === "removed" && <span>원본 삭제</span>}
        <span>{formatPages(card.filecount)}</span>
        {card.rating !== null && <span>{formatRating(card.rating)}</span>}
      </p>
      {children}
    </article>
  );
}
